<template>
  <div>
    <KkBox>
      <template #header>
        <div class="header">
          <KkButton :to="{ name: 'CompaniesServices', params: { company_uuid: companyUuid } }"
                    :text="$t('pages.companies.services.form.backToServices')"
                    :color="'secondary'"
          />
        </div>
      </template>
      <template #content>
        <ValidationObserver ref="companiesServicesValidation" tag="div" class="content">
          <div class="content__left">
            <div class="borderBoxWrapper">
              <KkBox :title="$t('pages.companies.services.form.servicePatternName.title')" border>
                <template #content>
                  <KkInfo>
                    <p>{{ $t('pages.companies.services.form.servicePatternName.info1') }}</p>
                  </KkInfo>
                  <div v-show="!servicePatternLoading" class="form">
                    <p>
                      <KkFormLabel :text="formatLabelText({
                        label: 'pages.companies.services.form.servicePatternName.title',
                        max: 140,
                        currentLength: servicePatternName.length,
                        required: true
                      })"
                      />
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, value, handleChange }"
                      v-model="servicePatternName"
                      :name="$t('pages.companies.services.form.servicePatternName.title')"
                      rules="required|max:140"
                    >
                      <KkTextField :model-value="value" @update:model-value="handleChange" />
                      <KkValidationError :text="errors[0]" />
                    </ValidationProvider>
                  </div>
                </template>
              </KkBox>
            </div>
            <div class="borderBoxWrapper">
              <KkBox :title="$t('pages.companies.services.form.service.title', { name: gmbCategory.name })" border>
                <template #content>
                  <Loading v-if="servicePatternLoading" />
                  <template v-else>
                    <ValidationObserver v-if="isOpenCustomServiceForm" ref="customServiceForm" slim>
                      <div class="form">
                        <p>
                          <KkFormLabel :text="formatLabelText({
                            label: 'pages.companies.services.form.serviceName.title',
                            max: 120,
                            currentLength: serviceName.length,
                            required: true,
                          })"
                          />
                        </p>
                        <template v-if="isEditingCustomService">
                          <ValidationProvider
                            v-slot="{ errors, value, handleChange }"
                            v-model="serviceName"
                            :name="$t('pages.companies.services.form.serviceName.title')"
                            rules="required|max:120"
                          >
                            <KkTextField :model-value="value" @update:model-value="handleChange" />
                            <KkValidationError :text="errors[0]" />
                          </ValidationProvider>
                          <p>{{ $t('pages.companies.services.form.serviceName.example') }}</p>
                        </template>
                        <p v-else class="serviceName">
                          {{ serviceName }}
                          <span class="serviceName__annotation">
                            {{ $t('pages.companies.services.form.serviceName.annotation') }}
                          </span>
                        </p>
                      </div>
                      <div class="form">
                        <p>
                          <KkFormLabel :text="formatLabelText({
                            label: 'pages.companies.services.form.priceSetting.title',
                            required: false
                          })"
                          />
                        </p>
                        <KkRadioGroup v-model="priceSetting" :items="priceOptions" name="priceSetting" />
                      </div>
                      <div v-if="priceSetting === 'set'" class="form">
                        <p>
                          <KkFormLabel :text="formatLabelText({ label: 'pages.companies.services.form.price.title' })" />
                        </p>
                        <ValidationProvider
                          v-slot="{ errors, value, handleChange }"
                          v-model="price"
                          :name="$t('pages.companies.services.form.price.title')"
                          rules="required|numeric"
                        >
                          <KkTextField type="number" :model-value="value" @update:model-value="handleChange" />
                          <KkValidationError :text="errors[0]" />
                          <div class="infoWrapper">
                            <KkInfo>
                              <p>{{ $t('pages.companies.services.form.price.info') }}</p>
                            </KkInfo>
                          </div>
                        </ValidationProvider>
                      </div>
                      <div class="form">
                        <p>
                          <KkFormLabel :text="formatLabelText({
                            label: 'pages.companies.services.form.description.title',
                            currentLength: description.length,
                            max: 300,
                            required: false,
                          })"
                          />
                        </p>
                        <KkTextarea v-model="description" />
                        <div class="infoWrapper">
                          <KkInfo>
                            <p>{{ $t('pages.companies.services.form.description.info') }}</p>
                          </KkInfo>
                        </div>
                      </div>
                      <div class="buttons">
                        <div class="buttonWrapper">
                          <KkButton :text="$t('labels.cancel')"
                                    color="secondary"
                                    @click="handleCancel"
                          />
                        </div>
                        <div class="buttonWrapper">
                          <KkButton
                            :text="$t('labels.confirm')"
                            @click="handleDetermine"
                          />
                        </div>
                      </div>
                    </ValidationObserver>
                    <template v-else>
                      <KkInfo>
                        <p>{{ $t('pages.companies.services.form.service.info1') }}</p>
                      </KkInfo>
                      <template v-if="filteredGmbServiceItems.length === 0 && servicePattern.services.length === 0">
                        <p class="serviceNotRegistered">{{ $t('pages.companies.services.form.service.serviceNotRegistered') }}</p>
                      </template>
                      <template v-if="servicePattern.services.length">
                        <div v-for="(service, index) in servicePattern.services" :key="'servicePattern' + index" class="serviceCard">
                          <KkBoxWithMenu
                            :menu-list="menuList"
                            @on-click-edit-menu="e => handleClickMenu({ type: e, index: index })"
                          >
                            <template #title>
                              <p class="serviceCard__header">
                                {{ service.name }}
                                <template v-if="service.price">
                                  <span v-if="service.price.price" class="price">
                                    {{ toNumberLocaleString(service.price.price) }}円
                                  </span>
                                  <span v-else class="price">
                                    {{ $t('pages.companies.services.form.free') }}
                                  </span>
                                </template>
                              </p>
                            </template>
                            <template #default>
                              <div class="serviceCard__body">
                                {{ service.description }}
                              </div>
                            </template>
                          </KkBoxWithMenu>
                        </div>
                      </template>
                      <div v-if="filteredGmbServiceItems.length" class="selectServiceButtonBox">
                        <KkAddCard v-for="item in filteredGmbServiceItems"
                                   :key="'serviceTypeId' + item.serviceTypeId"
                                   :name="item.name"
                                   class="selectServiceButton"
                                   @click="handleClickItem(item)"
                        />
                      </div>
                      <div class="addLabelWrapper">
                        <KkAddLabel
                          :label="$t('pages.companies.services.form.service.addLabel')"
                          @click="showCustomServiceForm"
                        />
                      </div>
                    </template>
                  </template>
                </template>
              </KkBox>
            </div>
          </div>
          <div class="content__right">
            <KkSelectStore
              :target-store-label="$t('pages.companies.services.form.selectStore.label')"
              :selected-store-count="storeUuids.length"
              :disabled="searchAttributesLoading || searchStoresLoading"
              @open-store-modal="handleOpenStoreModal"
            >
              <div class="innerSelectStore">
                <KkInfo>{{ $t('pages.companies.services.form.selectStore.info') }}</KkInfo>
                <div class="buttons">
                  <div class="buttonWrapper">
                    <KkButton :text="$t('labels.cancel')"
                              color="secondary"
                              @click="handleSelectStoreCancel"
                    />
                  </div>
                  <div class="buttonWrapper">
                    <KkButton
                      :disabled="servicePatternLoading || searchAttributesLoading || gmbCategoryLoading"
                      :text="$t('labels.confirm')"
                      @click="handleSelectStoreSubmit"
                    />
                  </div>
                </div>
              </div>
            </KkSelectStore>
          </div>
        </ValidationObserver>
      </template>
    </KkBox>
    <KkFilterModal
      :is-visible="modal"
      :filter-items="filterItems"
      :form-values="formValues"
      :total-count="totalCount"
      :submit-button-text="$t('labels.checkTargetStores')"
      @close="handleCloseFilterModal"
      @submit="handleSubmitFilterModal"
      @reset="handleResetFilterModal"
      @change-check-box="handleChangeFilterModalCheckBox"
    />
    <StoreModal
      v-if="showStoreModal"
      store-name="companiesServicePattern"
      @close-store-modal="confirmStoreUuids"
      @back-filter-modal="handleBack"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import {
  KkBox,
  KkButton,
  KkFormLabel,
  KkTextarea,
  KkTextField,
  KkRadioGroup,
  KkValidationError,
  KkAddCard,
  KkBoxWithMenu
} from '../../../components/base'
import { KkAddLabel, KkInfo } from '../../../components/ui'
import { KkFilterModal, KkSelectStore } from '../../../components/projects'
import { addAttributes } from '../../../modules/filterModalConst'
import StoreModal from '../../../components/StoreModal'
import Loading from '../../../components/Loading'
import formatLabelText from '../../../modules/formatLabelText'
import generateRandomStr from '../../../modules/generateRandomStr'
import { toNumberLocaleString } from '../../../modules/localeString'

export default {
  name: 'CompaniesServicesForm',
  components: {
    Loading,
    KkRadioGroup,
    KkSelectStore,
    KkTextarea,
    KkTextField,
    KkFormLabel,
    KkButton,
    KkBox,
    KkInfo,
    KkAddLabel,
    KkBoxWithMenu,
    KkFilterModal,
    KkAddCard,
    KkValidationError,
    StoreModal
  },
  emits: ['update-title', 'update-breadcrumbs'],
  data() {
    return {
      filterItems: [],
      showStoreModal: false,
      modal: false,
      isOpenCustomServiceForm: false,
      id: undefined,
      serviceTypeId: undefined,
      servicePatternName: '',
      serviceName: '',
      description: '',
      priceSetting: 'noSet',
      price: null,
      storeUuids: []
    }
  },
  computed: {
    ...mapState({
      gmbCategory: state => state.gmbCategory.data,
      gmbCategoryLoading: state => state.gmbCategory.loading,
      servicePattern: state => state.companiesServicePattern.data,
      servicePatternLoading: state => state.companiesServicePattern.loading,
      searchAttributesLoading: state => state.companiesSearchTagGroups.loading,
      formValues: state => state.companiesSearchTagGroups.formValues,
      companiesServicePatternFormValues: state => state.companiesServicePattern.formValues,
      changeCheckBox: state => state.companiesSearchTagGroups.changeCheckBox,
      totalCount: state => state.companiesGmbSearchStores.storeTotalCount,
      searchStoresLoading: state => state.companiesGmbSearchStores.loading,
      areas: state => state.companiesSearchTagGroups.data.areas,
      brands: state => state.companiesSearchTagGroups.data.brands,
      franchisees: state => state.companiesSearchTagGroups.data.franchisees,
      user_companies: state => state.companiesSearchTagGroups.data.user_companies,
      prefectures: state => state.companiesSearchTagGroups.data.prefectures,
      original_tag_group_a : state => state.companiesSearchTagGroups.data.original_tag_group_a,
      original_tag_group_b : state => state.companiesSearchTagGroups.data.original_tag_group_b,
      original_tag_group_c : state => state.companiesSearchTagGroups.data.original_tag_group_c,
      original_tag_group_d : state => state.companiesSearchTagGroups.data.original_tag_group_d,
      original_tag_group_e : state => state.companiesSearchTagGroups.data.original_tag_group_e,
    }),
    isDuplicatePage() { return this.$route.query.service_pattern_id },
    isEditPage() { return this.$route.params.id },
    isEditingCustomService() { return this.serviceTypeId == null },
    // すでに登録されたサービスを弾く
    filteredGmbServiceItems() {
      if (this.servicePattern.services.length === 0) return this.gmbCategory.gmbServiceItems

      return this.gmbCategory.gmbServiceItems.filter(item => {
        return this.servicePattern.services.every(service => service.serviceTypeId !== item.serviceTypeId)
      })
    },
    priceOptions() {
      return [
        {
          id: 'noSet',
          value: 'noSet',
          text: this.$t('pages.companies.services.form.priceSetting.priceOptions.noSet'),
        },
        {
          id: 'free',
          value: 'free',
          text: this.$t('pages.companies.services.form.priceSetting.priceOptions.free'),
        },
        {
          id: 'set',
          value: 'set',
          text: this.$t('pages.companies.services.form.priceSetting.priceOptions.set'),
        },
      ]
    },
    disableFilterItemTypes() {
      return ['topic_type']
    },
    companyUuid() { return this.$route.params.company_uuid },
    categoryId() { return this.$route.params.category_id },
    menuList() {
      return [
        {
          type: 'edit',
          text: this.$t('pages.companies.services.form.boxWithMenu.menuList.edit'),
        },
        {
          type: 'delete',
          text: this.$t('pages.companies.services.form.boxWithMenu.menuList.delete')
        },
      ]
    },
    query() {
      return {
        companyUuid: this.companyUuid,
        formValues: this.formValues,
        gmbCategoryIds: [this.categoryId],
        gmbMetadatas: ['can_modify_service_list']
      }
    }
  },
  async created() {
    this.initServicePattern()
    this.readGmbCategory({ categoryId: this.categoryId }).then(() => {
      this.updateMetadata()
    })
    if (this.isEditPage || this.isDuplicatePage) {
      this.readServicePattern(this.isEditPage || this.isDuplicatePage).then(() => {
        this.servicePatternName = this.servicePattern.name
        this.storeUuids = this.servicePattern.storeUuids
      })
    }
    this.readSearchTagGroups().then(() => {
      this.setFormValues(this.formInitialValues())
      this.createFilter()
      this.readStores(this.query)
    })
  },
  methods: {
    toNumberLocaleString,
    formatLabelText,
    ...mapActions({
      initServicePattern: 'companiesServicePattern/initServicePattern',
      readServicePattern: 'companiesServicePattern/readServicePattern',
      readGmbCategory: 'gmbCategory/readGmbCategory',
      readStores: 'companiesGmbSearchStores/readStores',
      setFormValues: 'companiesSearchTagGroups/setFormValues',
      readSearchTagGroups: 'companiesSearchTagGroups/readSearchTagGroups',
      upsertLocalService: 'companiesServicePattern/upsertLocalService',
      deleteLocalService: 'companiesServicePattern/deleteLocalService',
      createServicePattern: 'companiesServicePattern/createServicePattern',
      updateServicePattern: 'companiesServicePattern/updateServicePattern',
      setFormValuesOfServicePattern: 'companiesServicePattern/setFormValues'
    }),
    handleCloseFilterModal() {
      this.setFormValuesOfServicePattern({ stores: [...this.storeUuids] })
      this.modal = false
    },
    handleOpenStoreModal() {
      if (this.storeUuids.length !== 0) {
        this.showStoreModal = true
      } else {
        this.modal = true
      }
    },
    updateMetadata() {
      this.$emit('update-title', this.$t('pages.companies.services.form.title', { name: this.gmbCategory.name }))
      const breadcrumbs = [
        {
          name: this.$t('pages.CompaniesDashboards.title'),
          pathName: 'CompaniesDashboards',
          query: {},
        },
        {
          name: this.$t('pages.companies.services.title'),
          pathName: 'CompaniesServices',
          query: {},
        },
        {
          name: this.$t('pages.companies.services.form.title', { name: this.gmbCategory.name })
        }
      ]
      this.$emit('update-breadcrumbs', breadcrumbs)
    },
    initForm() {
      this.id = generateRandomStr()
      this.serviceTypeId = undefined
      this.serviceName = ''
      this.description = ''
      this.priceSetting = 'noSet'
      this.price = null
    },
    showCustomServiceForm() {
      this.initForm()
      this.isOpenCustomServiceForm = true
    },
    handleChangeFilterModalCheckBox({ kind, id }) {
      const newFormValues = Object.assign({}, this.formValues)

      if(newFormValues[kind].includes(id)) {
        newFormValues[kind] = newFormValues[kind].filter((_id) => _id !== id)
      } else {
        newFormValues[kind].push(id)
      }

      this.setFormValues(newFormValues)

      this.readStores(this.query)
    },
    handleSubmitFilterModal() {
      this.modal = false
      this.showStoreModal = true
    },
    handleResetFilterModal() {
      this.setFormValues(this.formInitialValues())
      this.readStores(this.query)
    },
    async handleSelectStoreSubmit() {
      const { valid } = await this.$refs.companiesServicesValidation.validate()
      if (!valid) {
        alert(this.$t('messages.invalid'))
        return
      }
      const isExistDuplicateStore = this.gmbCategory.servicePatterns.some(pattern => {
        if (pattern.id === Number(this.isEditPage)) return false

        return this.storeUuids.some(uuid => pattern.storeUuids.includes(uuid))
      })
      if (isExistDuplicateStore && !confirm(this.$t('pages.companies.services.form.duplicateConfirm'))) return
      const payload = {
        name: this.servicePatternName,
        gmbCategoryId: this.gmbCategory.categoryId,
        items: this.servicePattern.services,
        storeUuids: this.storeUuids
      }
      if (this.isEditPage) {
        await this.updateServicePattern(payload)
      } else {
        await this.createServicePattern(payload)
      }
      this.$router.push({ name: 'CompaniesServices', params: { company_uuid: this.companyUuid } })
    },
    handleSelectStoreCancel() {
      this.$router.push({ name: 'CompaniesServices', params: { company_uuid: this.companyUuid } })
    },
    async handleDetermine() {
      const { valid } = await this.$refs.customServiceForm.validate()
      if (!valid) {
        alert(this.$t('messages.invalid'))
        return
      }
      const service = {
        id: this.id,
        name: this.serviceName,
        serviceTypeId: this.serviceTypeId,
        description: this.description,
      }
      service.price = this.setPriceObject(this.priceSetting, this.price)
      this.upsertLocalService(service)
      this.isOpenCustomServiceForm = false
    },
    handleCancel() {
      this.initForm()
      this.isOpenCustomServiceForm = false
    },
    setPriceString(priceSetting, price) {
      switch (priceSetting) {
      case 'noSet':
        return null
      case 'free':
        return null
      case 'set':
        return price.price.toString()
      }
    },
    setPriceObject(priceSetting, price) {
      switch (priceSetting) {
      case 'noSet':
        return null
      case 'free':
        return {
          price: null
        }
      case 'set':
        return {
          price: Number(price)
        }
      }
    },
    evalPriceSetting(service) {
      if (service.price == null) return 'noSet'
      return service.price.price === null ? 'free' : 'set'
    },
    handleClickMenu({ type, index }) {
      const target = this.servicePattern.services[index]
      switch (type) {
      case 'edit':
        this.id = target.id
        this.serviceTypeId = target.serviceTypeId
        this.serviceName = target.name
        this.description = target.description
        this.priceSetting = this.evalPriceSetting(target)
        this.price = this.setPriceString(this.priceSetting, target.price)
        this.isOpenCustomServiceForm = true
        break
      case 'delete':
        this.deleteLocalService(target.id)
        break
      }
    },
    handleClickItem(item) {
      this.id = generateRandomStr()
      this.serviceTypeId = item.serviceTypeId
      this.serviceName = item.name
      this.description = ''
      this.priceSetting = 'noSet'
      this.price = null
      this.isOpenCustomServiceForm = true
    },
    formInitialValues() {
      return {
        brands: [],
        franchisees: [],
        user_companies: [],
        areas: [],
        prefectures: [],
        original_tag_group_a: [],
        original_tag_group_b: [],
        original_tag_group_c: [],
        original_tag_group_d: [],
        original_tag_group_e: []
      }
    },
    /**
     * この順番を担保する
     * ブランド
     * 立地
     * フランチャイジー
     * エリアマネージャー
     * オリジナル分析軸（設定されていれば）
     * 都道府県（一番下になっていること）
     */
    createFilter() {
      const attributes = [
        this.brands,
        this.areas,
        this.franchisees,
        this.user_companies,
      ]

      if(this.original_tag_group_a) attributes.push(this.original_tag_group_a)
      if(this.original_tag_group_b) attributes.push(this.original_tag_group_b)
      if(this.original_tag_group_c) attributes.push(this.original_tag_group_c)
      if(this.original_tag_group_d) attributes.push(this.original_tag_group_d)
      if(this.original_tag_group_e) attributes.push(this.original_tag_group_e)
      attributes.push(this.prefectures)

      this.filterItems = attributes.map((attr) => {
        if(attr && attr.kind){
          return addAttributes(attr, attr.kind)
        }
      })
    },
    handleBack() {
      this.showStoreModal = false
      this.modal = true
    },
    confirmStoreUuids(storeUuids) {
      this.showStoreModal = false
      this.storeUuids = storeUuids
    },
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../../stylesheets/biz_spa/foundation/_var.scss';
.borderBoxWrapper {
  margin-bottom: 32px;
}
.header {
  display: flex;
  justify-content: flex-end;
}
.content {
  display: flex;
  justify-content: space-around;
  &__left {
    width: 60%;
    padding-right: 32px;
  }
  &__right {
    width: 40%;
  }
}
.form:not(:first-child) {
  margin-top: 32px;
}
.serviceNotRegistered {
  margin: 24px 0;
}
.infoWrapper {
  margin-top: 24px;
}
.buttonWrapper:not(:first-child) {
  margin-left: 8px;
}
.serviceCard {
  &:not(:first-child) {
    margin-top: 8px;
  }
  &__body {
    color: $color-font-base;
  }
  &__header {
    display: flex;
    justify-content: space-between;
    .price {
      margin-right: 16px;
    }
  }
}
.addLabelWrapper {
  margin-top: 24px;
}
.buttons {
  margin-top: 32px;
  display: flex;
  justify-content: flex-end;
}
.innerSelectStore {
  margin-top: 32px;
}
.selectServiceButtonBox {
  margin-top: 24px;
}
.selectServiceButton {
  display: flex;
  align-items: center;
  padding: 16px;
  width: 100%;
  margin-bottom: 8px;
  border: 1px solid $color-base-c;
  &__icon {
    width: 16px;
    margin-right: 8px;
  }
}
.serviceName {
  font-weight: bold;
  font-size: 18px;
  &__annotation {
    font-size: 14px;
    font-weight: normal;
  }
}
</style>
